// Utility functions for Marketing Calendar

// Helper functions to handle both camelCase and snake_case field names
// Return empty string only if value doesn't exist, not if it's an empty string
export const getCreatedAt = (campaign: any): string => {
  // If this is a scheduled campaign, use the schedule date
  if (campaign._scheduleEntry?.date) {
    return campaign._scheduleEntry.date;
  }
  
  // If this is an expanded campaign item with a specific publish entry, use that date
  if (campaign._publishEntry?.date) {
    return campaign._publishEntry.date;
  }
  
  const value = campaign.createdAt || campaign.created_at;
  return (value && value.trim()) ? value : '';
};

export const getStartDate = (campaign: any): string => {
  const value = campaign.startDate || campaign.start_date;
  return (value && value.trim()) ? value : '';
};

export const getEndDate = (campaign: any): string => {
  const value = campaign.endDate || campaign.end_date;
  return (value && value.trim()) ? value : '';
};

export const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const getCampaignName = (campaign: any): string => {
  // NEW API: Get from campaign.title
  if (campaign.title) {
    return campaign.title;
  }
  
  // Fallback to campaignData.title
  if (campaign.campaignData?.title) {
    return campaign.campaignData.title;
  }
  return `Campaign ${campaign.id}`;
};

export const getCampaignPlatforms = (campaign: any): string[] => {
  // If this is a scheduled campaign, get platforms from scheduleData
  if (campaign._scheduleEntry?.channels && Array.isArray(campaign._scheduleEntry.channels)) {
    return campaign._scheduleEntry.channels.map((ch: any) => ch.name || ch);
  }
  
  // If this is a grouped calendar entry, use only platforms from the publish entries
  if (campaign._publishEntries && Array.isArray(campaign._publishEntries)) {
    return campaign._publishEntries
      .filter((pub: any) => pub.channel) // Ensure channel exists
      .map((pub: any) => pub.channel);
  }
  
  // NEW API: Get from publishData array (only posted ones)
  if (campaign.publishData && Array.isArray(campaign.publishData)) {
    return campaign.publishData
      .filter((pub: any) => pub.status === 'posted' && pub.channel)
      .map((pub: any) => pub.channel);
  }
  
  // Fallback to old structure
  if (campaign.platforms && Array.isArray(campaign.platforms)) {
    return campaign.platforms;
  }
  return [];
};

// Check if campaign is currently live (between validFrom and validTo AND posted)
export const isCampaignLive = (campaign: any): boolean => {
  // Must be posted first
  const hasPosted = campaign.publishData && 
                   Array.isArray(campaign.publishData) && 
                   campaign.publishData.length > 0 &&
                   campaign.publishData.some((pub: any) => pub.status === 'posted');
  
  if (!hasPosted) return false;
  
  const validFrom = campaign.campaignData?.validFrom;
  const validTo = campaign.campaignData?.validTo;
  
  if (!validFrom || !validTo) return false;
  
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  
  const startDate = new Date(validFrom);
  startDate.setHours(0, 0, 0, 0);
  
  const endDate = new Date(validTo);
  endDate.setHours(23, 59, 59, 999);
  
  return now >= startDate && now <= endDate;
};

// Get campaign status
export const getCampaignStatus = (campaign: any): string => {
  // Check if campaign has been posted
  const hasPosted = campaign.publishData && 
                   Array.isArray(campaign.publishData) && 
                   campaign.publishData.length > 0 &&
                   campaign.publishData.some((pub: any) => pub.status === 'posted');
  
  if (hasPosted) {
    // Check if it's live
    if (isCampaignLive(campaign)) {
      return 'live';
    }
    return 'posted';
  }
  
  return 'scheduled';
};

export const parseSocialMedia = (socialMedia: string): string[] => {
  try {
    if (socialMedia.startsWith('[')) {
      const parsed = JSON.parse(socialMedia);
      return Array.isArray(parsed) ? parsed : [socialMedia];
    }
    return socialMedia.split(',').map(p => p.trim());
  } catch {
    return socialMedia.split(',').map(p => p.trim());
  }
};

export const getMonthDates = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);

  const dates = [];
  const startDay = firstDay.getDay();

  for (let i = startDay - 1; i >= 0; i--) {
    const prevDate = new Date(firstDay);
    prevDate.setDate(prevDate.getDate() - (i + 1));
    dates.push({ date: prevDate, currentMonth: false });
  }

  for (let i = 1; i <= lastDay.getDate(); i++) {
    dates.push({ date: new Date(year, month, i), currentMonth: true });
  }

  const remainingDays = 42 - dates.length;
  for (let i = 1; i <= remainingDays; i++) {
    const nextDate = new Date(year, month + 1, i);
    dates.push({ date: nextDate, currentMonth: false });
  }

  return dates;
};

export const getWeekDates = (date: Date): Date[] => {
  const startOfWeek = new Date(date);
  const day = startOfWeek.getDay();
  startOfWeek.setDate(startOfWeek.getDate() - day);

  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const weekDate = new Date(startOfWeek);
    weekDate.setDate(weekDate.getDate() + i);
    weekDates.push(weekDate);
  }
  return weekDates;
};

export const getCampaignsForDate = (campaigns: any[], date: Date): any[] => {
  const dateStr = formatDateForInput(date);
  const expandedCampaigns: any[] = [];
  
  campaigns.forEach((campaign: any) => {
    const hasPublishData = campaign.publishData && Array.isArray(campaign.publishData) && campaign.publishData.length > 0;
    const hasScheduleData = campaign.scheduleData && campaign.scheduleData.scheduleDate;
    
    // Skip if no publish data or schedule data
    if (!hasPublishData && !hasScheduleData) {
      return;
    }
    
    // Check if campaign has been posted
    const hasBeenPosted = hasPublishData && campaign.publishData.some((entry: any) => entry.status === 'posted');
    
    // Process posted entries (only if campaign has been posted)
    if (hasBeenPosted) {
      const postedEntries = campaign.publishData.filter((entry: any) => {
        if (entry.status !== 'posted' || !entry.date) return false;
        
        const entryDate = new Date(entry.date);
        if (isNaN(entryDate.getTime())) return false;
        
        return formatDateForInput(entryDate) === dateStr;
      });
      
      if (postedEntries.length > 0) {
        // Group all entries from the same campaign on the same day together
        // Use the earliest entry as the representative
        const earliestEntry = postedEntries.reduce((earliest: any, entry: any) => {
          return new Date(entry.date) < new Date(earliest.date) ? entry : earliest;
        }, postedEntries[0]);
        
        // Create a single calendar entry for all platforms posted on this day
        expandedCampaigns.push({
          ...campaign,
          _publishEntries: postedEntries, // All entries posted on this day
          _publishEntry: earliestEntry, // For backward compatibility (shows earliest time)
          _uniqueKey: `${campaign.id}-posted-${dateStr}`, // Unique key based on campaign ID and date
          _type: 'posted'
        });
      }
    }
    
    // Process scheduled entries (ONLY if campaign hasn't been posted yet)
    if (hasScheduleData && !hasBeenPosted) {
      const scheduleDate = new Date(campaign.scheduleData.scheduleDate);
      if (!isNaN(scheduleDate.getTime()) && formatDateForInput(scheduleDate) === dateStr) {
        // Create scheduled entries for each channel
        const scheduledChannels = campaign.scheduleData.channels || [];
        
        if (scheduledChannels.length > 0) {
          expandedCampaigns.push({
            ...campaign,
            _scheduleEntry: {
              date: campaign.scheduleData.scheduleDate,
              channels: scheduledChannels
            },
            _uniqueKey: `${campaign.id}-scheduled-${campaign.scheduleData.scheduleDate}`,
            _type: 'scheduled'
          });
        }
      }
    }
  });
  
  return expandedCampaigns;
};
